import React, { useMemo } from 'react';
import Layout from '../components/Layout';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Filter, Download, Calendar, User } from 'lucide-react';
import usePonctualite from '../hooks/usePonctualite';
import useEnseignantData from '../hooks/useEnseignantData';
import PonctualiteTable from '../components/ponctualite/PonctualiteTable';
import PonctualiteForm from '../components/ponctualite/PonctualiteForm';
import StatistiquesAbsences from '../components/ponctualite/StatistiquesAbsences';
import PonctualiteSearchBar from '../components/ponctualite/PonctualiteSearchBar';
import ConfirmationModal from '../components/ConfirmationModal';

const PonctualitePage = () => {
  const navigate = useNavigate();
  
  // Hook pour gérer les données spécifiques aux enseignants
  const { resetEnseignantData } = useEnseignantData();

  // États pour la recherche
  const [searchTerm, setSearchTerm] = React.useState('');
  const [searchFilters, setSearchFilters] = React.useState({
    dateDebut: '',
    dateFin: '',
    classe: '',
    module: '',
    statut: '',
    volumeMin: '',
    volumeMax: ''
  });
  const [showAdvancedFilters, setShowAdvancedFilters] = React.useState(false);

  // Hook principal pour la gestion de la ponctualité
  const {
    // States
    activeTab,
    showModal,
    showDeleteModal,
    itemToDelete,
    isDeleting,
    isEditing,
    absencesEtudiants,
    absencesEnseignants,
    loading,
    classes,
    niveaux,
    filieres,
    modules,
    etudiants,
    enseignants,
    selectedClasse,
    selectedModule,
    currentPage,
    totalPages,
    showFilters,
    filters,
    formData,
    
    // Setters
    setActiveTab,
    setShowModal,
    setShowDeleteModal,
    setSelectedClasse,
    setSelectedModule,
    setCurrentPage,
    setShowFilters,
    setFilters,
    setFormData,
    
    // Actions
    handleSubmit,
    handleDelete,
    confirmDelete,
    handleEdit,
    formatDate,
    resetForm
  } = usePonctualite();



  // Fonction pour changer d'onglet avec reset des données
  const handleTabChange = (newTab) => {
    if (newTab !== activeTab) {
      resetEnseignantData(); // Reset les données d'enseignant
      resetForm(); // Reset le formulaire
    }
    setActiveTab(newTab);
  };

  const handleRefresh = () => {
    // Déclencher le rafraîchissement des données
    window.location.reload();
  };

  // Optimisation du rendu avec useMemo
  const currentData = useMemo(() => {
    return activeTab === 'etudiants' ? absencesEtudiants : absencesEnseignants;
  }, [activeTab, absencesEtudiants, absencesEnseignants]);

  // Fonction de filtrage des données
  const filteredData = useMemo(() => {
    let filtered = currentData;

    // Filtrage par terme de recherche
    if (searchTerm) {
      filtered = filtered.filter(item => {
        const nom = activeTab === 'etudiants' 
          ? `${item.etudiant?.nom || ''} ${item.etudiant?.prenom || ''}`
          : `${item.enseignant?.nom || ''} ${item.enseignant?.prenom || ''}`;
        
        return nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
               (item.classe_nom || item.matiere?.classe?.libelle || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
               (item.matiere?.module?.libelle || item.matiere?.libelle || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
               (item.motif || '').toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    // Filtres avancés
    if (searchFilters.dateDebut) {
      filtered = filtered.filter(item => new Date(item.date_absence || item.date) >= new Date(searchFilters.dateDebut));
    }
    if (searchFilters.dateFin) {
      filtered = filtered.filter(item => new Date(item.date_absence || item.date) <= new Date(searchFilters.dateFin));
    }
    if (searchFilters.classe) {
      filtered = filtered.filter(item => item.classe_id === parseInt(searchFilters.classe));
    }
    if (searchFilters.module) {
      filtered = filtered.filter(item => item.module_id === parseInt(searchFilters.module));
    }
    if (searchFilters.statut) {
      if (searchFilters.statut === 'absent') {
        filtered = filtered.filter(item => item.absence);
      } else if (searchFilters.statut === 'present') {
        filtered = filtered.filter(item => !item.absence);
      }
    }
    if (searchFilters.volumeMin) {
      filtered = filtered.filter(item => item.volume_horaire >= parseInt(searchFilters.volumeMin));
    }
    if (searchFilters.volumeMax) {
      filtered = filtered.filter(item => item.volume_horaire <= parseInt(searchFilters.volumeMax));
    }

    return filtered;
  }, [currentData, searchTerm, searchFilters, activeTab]);

  // Vérification si on doit afficher l'état vide
  const showEmptyState = useMemo(() => {
    return filteredData.length === 0;
  }, [filteredData.length]);

  // Vérification si on doit afficher le tableau
  const showTable = useMemo(() => {
    return filteredData.length > 0;
  }, [filteredData.length]);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
            <span className="ml-3 text-gray-600">Chargement des données...</span>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-8">
          {/* Card principale avec design professionnel */}
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Header avec bannière bleue */}
            <div className="bg-gradient-to-r from-[#202654] to-[#202654]/90 px-6 py-8">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                <div className="flex items-center text-white">
                  <button
                    onClick={() => navigate(-1)}
                    className="mr-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  <div>
                    <h1 className="text-2xl font-bold">Gestion de la Ponctualité</h1>
                    <p className="mt-1 text-blue-100">
                      Gérez les absences des étudiants et enseignants
                    </p>
                  </div>
                </div>
                <div className="mt-4 md:mt-0 flex flex-col sm:flex-row sm:space-x-3 space-y-3 sm:space-y-0">
                  <button
                    onClick={handleRefresh}
                    className="inline-flex items-center justify-center px-4 py-2 border border-white/20 rounded-md shadow-sm text-sm font-medium text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-200"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Actualiser
                  </button>
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="inline-flex items-center justify-center px-4 py-2 border border-white/20 rounded-md shadow-sm text-sm font-medium text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-200"
                  >
                    <Filter className="h-4 w-4 mr-2" />
                    Filtres
                  </button>
                  <button
                    onClick={() => setShowModal(true)}
                    className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-all duration-200"
                  >
                    <Plus className="h-5 w-5 mr-2" />
                    Nouvelle Absence
                  </button>
                </div>
              </div>
            </div>

            {/* Onglets intégrés dans le design */}
            <div className="border-b border-gray-200 bg-white">
              <nav className="flex">
                <button
                  className={`px-6 py-3 text-sm font-semibold focus:outline-none transition-all duration-200 ${
                    activeTab === 'etudiants' 
                      ? 'border-b-4 border-[#202654] text-[#202654] bg-white rounded-t-lg shadow-sm' 
                      : 'text-gray-500 hover:text-[#202654] hover:bg-gray-100'
                  }`}
                  style={{ marginRight: '-2px' }}
                  onClick={() => handleTabChange('etudiants')}
                >
                  <User className="h-4 w-4 mr-2 inline" />
                  Absences Étudiants
                </button>
                <button
                  className={`px-6 py-3 text-sm font-semibold focus:outline-none transition-all duration-200 ${
                    activeTab === 'enseignants' 
                      ? 'border-b-4 border-[#202654] text-[#202654] bg-white rounded-t-lg shadow-sm' 
                      : 'text-gray-500 hover:text-[#202654] hover:bg-gray-100'
                  }`}
                  onClick={() => handleTabChange('enseignants')}
                >
                  <Calendar className="h-4 w-4 mr-2 inline" />
                  Absences Enseignants
                </button>
              </nav>
            </div>



            {/* Section des données */}
            <div className="p-6">
              {/* Barre de recherche */}
              <div className="mb-4">
                <PonctualiteSearchBar
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  searchFilters={searchFilters}
                  setSearchFilters={setSearchFilters}
              activeTab={activeTab}
                  classes={classes}
                  modules={modules}
                  showAdvancedFilters={showAdvancedFilters}
                  setShowAdvancedFilters={setShowAdvancedFilters}
                />
                
                {/* Indicateur de résultats */}
                {(searchTerm || Object.values(searchFilters).some(filter => filter)) && (
                  <div className="mt-2 text-sm text-gray-600">
                    {filteredData.length} résultat{filteredData.length !== 1 ? 's' : ''} trouvé{filteredData.length !== 1 ? 's' : ''}
                  </div>
                )}
              </div>

              {/* État vide */}
            {showEmptyState && (
              <div className="p-12 text-center">
                <div className="max-w-md mx-auto">
                  <div className="p-4 bg-gray-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                    <svg className="h-8 w-8 text-[#202654]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Aucune absence enregistrée
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Commencez par enregistrer une absence pour {activeTab === 'etudiants' ? 'un étudiant' : 'un enseignant'}.
                  </p>
                  <button
                    onClick={() => setShowModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#202654] hover:bg-[#202654]/90"
                  >
                    <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Nouvelle Absence
                  </button>
                </div>
              </div>
            )}

            {/* Tableau des absences (si données disponibles) */}
            {showTable && (
              <PonctualiteTable
                activeTab={activeTab}
                  absencesEtudiants={filteredData}
                  absencesEnseignants={filteredData}
                handleEdit={handleEdit}
                handleDelete={handleDelete}
                formatDate={formatDate}
              />
            )}

            {/* Pagination conditionnelle */}
            {totalPages > 1 && (
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-t border-gray-200 mt-6">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Page <span className="font-medium">{currentPage}</span> sur{' '}
                    <span className="font-medium">{totalPages}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Précédent
                    </button>
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Suivant
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

            {/* Section des statistiques */}
            <div className="bg-gray-50 border-t border-gray-200 p-6">
              <StatistiquesAbsences
                absencesEtudiants={absencesEtudiants}
                absencesEnseignants={absencesEnseignants}
                activeTab={activeTab}
              />
            </div>
          </div>


        </div>

        {/* Formulaire d'ajout/modification */}
        <PonctualiteForm
          showModal={showModal}
          setShowModal={setShowModal}
          activeTab={activeTab}
          formData={formData}
          setFormData={setFormData}
          selectedClasse={selectedClasse}
          setSelectedClasse={setSelectedClasse}
          selectedModule={selectedModule}
          setSelectedModule={setSelectedModule}
          classes={classes}
          etudiants={etudiants}
          enseignants={enseignants}
          modules={modules}
          handleSubmit={handleSubmit}
          resetForm={resetForm}
          isEditing={isEditing}
        />

        {/* Modal de confirmation pour la suppression */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => {
            setShowDeleteModal(false);
          }}
          onConfirm={confirmDelete}
          title="Supprimer l'absence"
          message="Êtes-vous sûr de vouloir supprimer cette absence ? Cette action est irréversible."
          confirmText="Supprimer"
          cancelText="Annuler"
          type="danger"
          isLoading={isDeleting}
        />
      </div>
    </Layout>
  );
};

export default PonctualitePage;
